import { AsyncPipe } from '@angular/common';
import { Component, computed, inject, input, output } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { userEnums } from '@vectronic/openApi-admin';
import { userDtos } from '@vectronic/openApi-customer';
import {
  DatatableColumn,
  DatatableComponent,
  DatatableConfig,
  DatatableListWithActionsSettings,
} from '@vectronic/table';
import { AlertComponent, ConfirmationDialogComponent } from '@vectronic/ui';
import { DateTimeHelper, DialogService, DropOffDataTable } from '@vectronic/util';
import { Observable } from 'rxjs';

@Component({
  selector: 'rc-dropoff-schedule',
  templateUrl: './dropoff-schedule.component.html',
  styleUrls: ['./dropoff-schedule.component.scss'],
  imports: [AlertComponent, DatatableComponent, MatButton, MatTooltip, MatIcon, AsyncPipe],
})
export class DropoffScheduleComponent {
  dialogService = inject(DialogService);

  readonly = input<boolean>();
  loading = input<boolean>();
  collarsWithNoShortHeaderFormat = input<number[]>();
  dropOffIds = input<number[]>();
  selectedDropOffData$ = input<Observable<DropOffDataTable[]>>();
  selectedDropOffsIds = input<number[]>();
  isDropOffEnabled = input<boolean>();
  devicesWithoutRadioControl = input<number[]>();
  devicesWithoutDeviceManagerPermission = input<number[]>();

  isMaxDropOffsReached = input<boolean>();
  fireDropOffTooltip = input<string>('Fire Drop Off for selection');

  addShare = output<number>();
  editShare = output<{ idDevice: number; email: string; oldRole: userEnums.Role }>();
  deleteShare = output<{ idDevice: number; email: string }>();
  remove = output<DropOffDataTable>();
  addDropOff = output<{ row: DropOffDataTable; dropOffId: string }>();
  submitFireDropOff = output();

  private columns = computed<DatatableColumn<DropOffDataTable>[]>(() => [
    {
      headerName: 'Collar ID',
      fieldName: 'idDevice',
      columnId: 0,
    },
    {
      headerName: 'Animal',
      fieldName: 'animalName',
      columnId: 1,
      headerHoverText: 'Currently assigned animal',
    },
    {
      headerName: 'Last Reception',
      fieldName: 'deviceMetaInformation',
      translator: ({ deviceMetaInformation }) => {
        if (!deviceMetaInformation || !deviceMetaInformation.lastReception) return '';
        return DateTimeHelper.formatDateTime(deviceMetaInformation.lastReception, true);
      },
      columnId: 2,
      headerHoverText: 'Time of last received message',
    },
    {
      headerName: 'Drop Off ID',
      fieldName: 'dropOff',
      component: {
        name: 'DatatableDropOffComponent',
        input: { row: (row) => row, dropOffIds: () => this.dropOffIds() },
        output: { addDropOffId: (event) => this.addDropOff.emit(event) },
      },
      columnId: 3,
      headerHoverText: 'Attached Drop Off',
    },
    {
      fieldName: 'dropOffType',
      columnId: 4,
    },
    {
      fieldName: 'admins',
      component: {
        name: 'DatatableListWithActionsComponent',
        input: {
          settings: (row): DatatableListWithActionsSettings => ({
            showAddButton: true,
            showEditButton: true,
            showDeleteButton: true,
            list: row.admins,
            toStringTranslator: ({ email, role }: userDtos.Share) => `${email}, ${role}`,
            toClipboardTranslator: ({ email }: userDtos.Share) => email,
          }),
          readonly: () => this.readonly(),
        },
        output: {
          buttonClicked: (event, row) => this.listActionButtonClicked(event, row),
        },
      },
      columnId: 5,
      headerHoverText: 'Owners and Admins of the collars only in Admin Tools',
    },
  ]);

  datatableConfig = computed<DatatableConfig<DropOffDataTable>>(() => ({
    uniqueId: 'drop_off_remote_command',
    title: 'Current Selection',
    columns: this.columns(),
    disablePagination: true,
    disableEditColumns: true,
    disableSort: true,
    rowActions: [
      {
        matIcon: 'close',
        label: 'Remove from selection',
        onClick: (row) => this.onDeleteDropOff(row),
        doHide: () => this.readonly(),
        color: 'warn',
      },
    ],
  }));

  onDeleteDropOff(row: DropOffDataTable) {
    this.dialogService.open(ConfirmationDialogComponent, {
      data: {
        title: 'Remove Drop Off',
        message: 'Are you sure to remove this Drop Off from the selection?',
      },
      afterConfirm: () => this.remove.emit(row),
      config: { disableClose: false },
    });
  }

  listActionButtonClicked(
    { type, element }: { type: 'edit' | 'delete' | 'add'; element: any },
    { idDevice }: DropOffDataTable,
  ) {
    const { email, role } = element ?? {};
    if (type === 'add') this.addShare.emit(idDevice);
    else if (type === 'edit') this.editShare.emit({ idDevice, email, oldRole: role });
    else if (type === 'delete') this.deleteShare.emit({ idDevice, email });
  }

  isMaxDropOffsReachedWithShortHeaderFormat() {
    return this.isMaxDropOffsReached() && !this.selectedDropOffsIds()?.length;
  }

  collarsWithShortHeaderFormatJoin() {
    return this.collarsWithNoShortHeaderFormat()?.join(', ');
  }

  isDisabled() {
    return this.readonly() || !this.isDropOffEnabled();
  }
}
